// Arte del estudio (Fase 9.2): salas, personas y mobiliario en SVG isométrico.
// Arte propio; el tema decide colores, nombres y variantes (cabina o escritorio).
import React from 'react';
import { Box, Furniture, Label, Poly, useIso } from './iso';

export const WALL_H = 1.35;

// ── Sala: losa, piso, dos paredes del fondo y decoración ─────────────────────
export function Room({ x, y, w, d, children }) {
  const { c, theme } = useIso();
  const H = WALL_H;
  const T = 0.14;   // grosor del borde superior de las paredes
  const lines = [];
  for (let i = 1; i < w; i++) lines.push(<Poly key={`gx${i}`} pts={[[x + i, y, 0], [x + i, y + d, 0]]} stroke={c.floorLine} fill="none" />);
  for (let j = 1; j < d; j++) lines.push(<Poly key={`gy${j}`} pts={[[x, y + j, 0], [x + w, y + j, 0]]} stroke={c.floorLine} fill="none" />);

  return (
    <g>
      <Box x={x} y={y} z={-0.18} w={w} d={d} h={0.18} top={c.floor} left={c.wallSide} right={c.furnitureSide} />
      <Poly pts={[[x, y, 0], [x + w, y, 0], [x + w, y + d, 0], [x, y + d, 0]]} fill={c.floor} className="studio-room-floor" />
      {lines}
      {/* Pared del fondo derecha (plano y = y) y del fondo izquierda (plano x = x) */}
      <Poly pts={[[x, y, 0], [x + w, y, 0], [x + w, y, H], [x, y, H]]} fill={c.wallBack} />
      <Poly pts={[[x, y, 0], [x, y + d, 0], [x, y + d, H], [x, y, H]]} fill={c.wallSide} />
      <Poly pts={[[x - T, y - T, H], [x + w, y - T, H], [x + w, y, H], [x, y, H], [x, y + d, H], [x - T, y + d, H]]} fill={c.wallRim} />
      <Poly pts={[[x + w, y - T, 0], [x + w, y, 0], [x + w, y, H], [x + w, y - T, H]]} fill={c.wallRim} opacity={0.8} />
      <Poly pts={[[x - T, y + d, 0], [x, y + d, 0], [x, y + d, H], [x - T, y + d, H]]} fill={c.wallRim} opacity={0.6} />
      {theme.decor === 'acoustic' ? <Acoustic x={x} y={y} w={w} d={d} /> : <Windows x={x} y={y} w={w} d={d} />}
      {children}
    </g>
  );
}

// Paneles acústicos (tema estudio)
function Acoustic({ x, y, w, d }) {
  const { c } = useIso();
  const out = [];
  for (let px = x + 0.35, i = 0; px + 0.75 <= x + w - 0.2; px += 1.05, i++) {
    out.push(<Poly key={`b${i}`} pts={[[px, y, 0.3], [px + 0.75, y, 0.3], [px + 0.75, y, 1.1], [px, y, 1.1]]} fill={i % 2 ? c.panel : c.panelAlt} stroke="rgba(0,0,0,0.25)" />);
  }
  for (let py = y + 0.35, i = 0; py + 0.75 <= y + d - 0.2; py += 1.05, i++) {
    out.push(<Poly key={`s${i}`} pts={[[x, py, 0.3], [x, py + 0.75, 0.3], [x, py + 0.75, 1.1], [x, py, 1.1]]} fill={i % 2 ? c.panelAlt : c.panel} stroke="rgba(0,0,0,0.25)" opacity={0.85} />);
  }
  return <g>{out}</g>;
}

// Ventanas (tema oficina)
function Windows({ x, y, w, d }) {
  const { c } = useIso();
  const out = [];
  for (let px = x + 0.5, i = 0; px + 1.2 <= x + w - 0.3; px += 1.8, i++) {
    out.push(<Poly key={`b${i}`} pts={[[px, y, 0.45], [px + 1.2, y, 0.45], [px + 1.2, y, 1.15], [px, y, 1.15]]} fill={c.panel} stroke={c.glassEdge} />);
  }
  for (let py = y + 0.5, i = 0; py + 1.2 <= y + d - 0.3; py += 1.8, i++) {
    out.push(<Poly key={`s${i}`} pts={[[x, py, 0.45], [x, py + 1.2, 0.45], [x, py + 1.2, 1.15], [x, py, 1.15]]} fill={c.panelAlt} stroke={c.glassEdge} opacity={0.85} />);
  }
  return <g>{out}</g>;
}

// ── Personas ─────────────────────────────────────────────────────────────────
/**
 * kind: 'ai' (agente de IA, con audífonos y visor), 'human' o 'lead' (cliente en espera).
 * seated: sin piernas (detrás de un escritorio o en un sofá).
 */
export function Person({ x, y, z = 0, kind = 'human', color, seated = false, headset = false, glow = false }) {
  const { P, s, c } = useIso();
  const [px, py] = P(x, y, z);
  const u = s * 0.9;
  const skin = kind === 'ai' ? c.aiSkin : c.skin;
  const shirt = color || (kind === 'ai' ? c.ai : c.lead);
  const lift = seated ? 0.3 * u : 0;
  return (
    <g transform={`translate(${px.toFixed(1)},${(py + lift).toFixed(1)})`}>
      <ellipse cx={0} cy={-lift} rx={0.3 * u} ry={0.15 * u} fill="#000" opacity={0.3} />
      {!seated && <rect x={-0.15 * u} y={-0.4 * u} width={0.3 * u} height={0.4 * u} rx={0.06 * u} fill="#1e1b2b" />}
      <rect x={-0.22 * u} y={-0.98 * u} width={0.44 * u} height={0.62 * u} rx={0.19 * u} fill={shirt} />
      <circle cx={0} cy={-1.17 * u} r={0.2 * u} fill={skin} />
      {kind === 'ai' && (
        <rect x={-0.15 * u} y={-1.22 * u} width={0.3 * u} height={0.08 * u} rx={0.04 * u}
          fill={glow ? '#fff' : c.screenGlow} opacity={glow ? 1 : 0.85} />
      )}
      {headset && (
        <g fill="none" stroke="#111827" strokeWidth={0.06 * u} strokeLinecap="round">
          <path d={`M ${-0.21 * u} ${-1.17 * u} A ${0.22 * u} ${0.24 * u} 0 0 1 ${0.21 * u} ${-1.17 * u}`} />
          <rect x={-0.27 * u} y={-1.24 * u} width={0.1 * u} height={0.16 * u} rx={0.04 * u} fill="#111827" />
          <rect x={0.17 * u} y={-1.24 * u} width={0.1 * u} height={0.16 * u} rx={0.04 * u} fill="#111827" />
        </g>
      )}
    </g>
  );
}

// ── Mobiliario ───────────────────────────────────────────────────────────────
export function Monitor({ x, y, z, w = 0.8, lit = true }) {
  const { c } = useIso();
  const h = 0.48;
  return (
    <g>
      <Box x={x + w / 2 - 0.05} y={y + 0.02} z={z} w={0.1} d={0.06} h={0.14} top={c.furnitureSide} left={c.furnitureSide} right={c.furnitureSide} />
      <Box x={x} y={y} z={z + 0.14} w={w} d={0.07} h={h} top={c.furnitureSide} left={c.screen} right={c.furnitureSide} />
      {lit && <Poly pts={[[x + 0.06, y + 0.071, z + 0.2], [x + w - 0.06, y + 0.071, z + 0.2], [x + w - 0.06, y + 0.071, z + 0.14 + h - 0.06], [x + 0.06, y + 0.071, z + 0.14 + h - 0.06]]} fill={c.screenGlow} opacity={0.35} />}
    </g>
  );
}

/** Letrero ON AIR (o su equivalente del tema) sobre un punto 3D. */
export function OnAirSign({ at, lit, pulse, size = 1 }) {
  const { P, s, c, theme } = useIso();
  const [x, y] = P(...at);
  const u = s * 0.9 * size;
  const w = 1.35 * u;
  const h = 0.38 * u;
  return (
    <g transform={`translate(${x.toFixed(1)},${y.toFixed(1)})`} className={pulse ? 'studio-pulse' : undefined}>
      {lit && <rect x={-w / 2 - 4} y={-h - 4} width={w + 8} height={h + 8} rx={6} fill={c.onAir} opacity={0.25} />}
      <rect x={-w / 2} y={-h} width={w} height={h} rx={0.06 * u} fill={lit ? c.onAir : c.onAirOff} stroke="rgba(0,0,0,0.4)" />
      <text x={0} y={-h * 0.28} fontSize={0.26 * u} fontWeight={800} textAnchor="middle" letterSpacing={0.5}
        fill={lit ? '#fff' : 'rgba(255,255,255,0.28)'} style={{ pointerEvents: 'none' }}>{theme.words.onAir}</text>
    </g>
  );
}

/** Luz de clip: parpadea tras un error o un rechazo de la política. */
export function ClipLed({ at, lit }) {
  const { P, s, c, theme } = useIso();
  const [x, y] = P(...at);
  const u = s * 0.9;
  return (
    <g transform={`translate(${x.toFixed(1)},${y.toFixed(1)})`}>
      <g className={lit ? 'studio-blink' : undefined}>
        {lit && <circle r={0.2 * u} fill={c.clip} opacity={0.3} />}
        <circle r={0.1 * u} fill={lit ? c.clip : '#3a1a22'} stroke="rgba(0,0,0,0.5)" />
      </g>
      {lit && (
        <text x={0.16 * u} y={0.08 * u} fontSize={0.2 * u} fontWeight={700} fill={lit ? c.clip : c.muted}
          stroke={c.halo} strokeWidth={2.5} paintOrder="stroke" style={{ pointerEvents: 'none' }}>{theme.words.clip}</text>
      )}
    </g>
  );
}

/** Vúmetro vertical de 7 segmentos (verde → rojo). */
export function VuMeter({ at, level }) {
  const { P, s, c } = useIso();
  const [x, y] = P(...at);
  const u = s * 0.9;
  const seg = 0.11 * u;
  return (
    <g transform={`translate(${x.toFixed(1)},${y.toFixed(1)})`}>
      <rect x={-0.05 * u} y={-7 * (seg + 1.5) - 0.05 * u} width={0.28 * u} height={7 * (seg + 1.5) + 0.1 * u} rx={2} fill={c.rack} stroke={c.rackEdge} />
      {c.vu.map((col, i) => (
        <rect key={i} x={0} y={-(i + 1) * (seg + 1.5)} width={0.18 * u} height={seg} rx={1}
          fill={i < level ? col : 'rgba(255,255,255,0.06)'} />
      ))}
    </g>
  );
}

/** Nivel del vúmetro (0 a 7) a partir de los turnos de los últimos 15 min. */
export const vuLevel = (turns, busy) => Math.min(7, Math.ceil(turns / 2) + (busy ? 1 : 0));

/** Globo con un número (tareas de un agente o casos de un asesor). */
export function CountBadge({ at, count, color, textColor = '#fff', title }) {
  const { P } = useIso();
  const [x, y] = P(...at);
  const w = count > 9 ? 22 : 17;
  return (
    <g transform={`translate(${x.toFixed(1)},${y.toFixed(1)})`}>
      {title && <title>{title}</title>}
      <rect x={-w / 2} y={-8.5} width={w} height={17} rx={8.5} fill={color} stroke="rgba(0,0,0,0.45)" />
      <text y={4} fontSize={10.5} fontWeight={800} textAnchor="middle" fill={textColor} style={{ pointerEvents: 'none' }}>{count}</text>
    </g>
  );
}

/** Marca en el piso del puesto seleccionado. */
export function Selection({ x, y, w, d }) {
  const { c } = useIso();
  return (
    <Poly pts={[[x - 0.1, y - 0.1, 0.01], [x + w + 0.1, y - 0.1, 0.01], [x + w + 0.1, y + d + 0.1, 0.01], [x - 0.1, y + d + 0.1, 0.01]]}
      fill={c.accent} opacity={0.18} stroke={c.accent} sw={2.5} className="studio-pulse" />
  );
}

// ── Sala de control ──────────────────────────────────────────────────────────
export function ControlRoom({ room, agents, lightsOf }) {
  const { c, theme } = useIso();
  const { x, y, w } = room;
  const cx = x + w / 2;
  const lead = agents[0];
  const l = lead ? lightsOf(lead.id) : { busy: false, flash: false, clip: false };
  const onAir = l.busy || l.flash;
  const busyAny = agents.some(a => lightsOf(a.id).busy);
  const deskW = Math.min(3.8, w - 1.6);
  return (
    <g>
      <OnAirSign at={[cx, y, 1.55]} lit={onAir || busyAny} pulse={l.busy} size={1.2} />
      <ClipLed at={[cx + 1.1, y, 1.42]} lit={l.clip} />
      {lead && <Person x={cx} y={y + 1.35} kind="ai" color={c.ai} headset glow={onAir} />}
      {theme.control === 'console' ? (
        <g>
          <Furniture x={cx - deskW / 2} y={y + 2.0} w={deskW} d={1.15} h={0.72} />
          {Array.from({ length: Math.floor(deskW / 0.32) }, (_, i) => (
            <Poly key={i} pts={[[cx - deskW / 2 + 0.25 + i * 0.32, y + 2.2, 0.73], [cx - deskW / 2 + 0.25 + i * 0.32, y + 2.75, 0.73]]}
              stroke={i % 4 === 0 ? c.accent : c.muted} sw={1.4} fill="none" />
          ))}
          <Monitor x={cx - deskW / 2 + 0.25} y={y + 2.02} z={0.72} w={0.95} lit />
          <Monitor x={cx + deskW / 2 - 1.2} y={y + 2.02} z={0.72} w={0.95} lit />
        </g>
      ) : (
        <g>
          <Furniture x={cx - deskW / 2} y={y + 2.0} w={deskW} d={1.0} h={0.7} />
          <Monitor x={cx - 0.5} y={y + 2.02} z={0.7} w={1.0} lit />
        </g>
      )}
      <VuMeter at={[x + w - 0.5, y + 3.4, 0]} level={lead ? vuLevel(lead.turns_15m, l.busy) : 0} />
    </g>
  );
}

// ── Cabina (o puesto) de un agente de IA ─────────────────────────────────────
export const BOOTH = 2.2;

export function AgentStation({ x, y, agent, lights, selected }) {
  const { c, theme } = useIso();
  const B = BOOTH;
  const onAir = lights.busy || lights.flash;
  const glassTint = lights.clip ? 'rgba(244,63,94,0.16)' : c.glass;
  const glassEdge = lights.clip ? c.clip : c.glassEdge;
  const cx = x + B / 2;

  const body = theme.station === 'cabin' ? (
    <g>
      <Box x={x} y={y} w={B} d={B} h={0.06} top={c.furnitureSide} left={c.furnitureSide} right={c.rack} />
      <Poly pts={[[x, y, 0.06], [x + B, y, 0.06], [x + B, y, 1.3], [x, y, 1.3]]} fill={glassTint} stroke={glassEdge} sw={1} />
      <Poly pts={[[x, y, 0.06], [x, y + B, 0.06], [x, y + B, 1.3], [x, y, 1.3]]} fill={glassTint} stroke={glassEdge} sw={1} opacity={0.8} />
      <Person x={cx} y={y + 0.85} kind="ai" color={c.ai} headset glow={onAir} />
      {/* Micrófono con brazo */}
      <Poly pts={[[cx + 0.45, y + 1.45, 0.06], [cx + 0.45, y + 1.45, 1.05]]} stroke="#9ca3af" sw={1.6} fill="none" />
      <Poly pts={[[cx + 0.45, y + 1.45, 1.05], [cx + 0.1, y + 1.2, 1.12]]} stroke="#9ca3af" sw={1.6} fill="none" />
      <Box x={cx} y={y + 1.12} z={1.02} w={0.14} d={0.14} h={0.22} top="#e5e7eb" left="#6b7280" right="#4b5563" />
    </g>
  ) : (
    <g>
      <Person x={cx - 0.1} y={y + 0.75} kind="ai" color={c.ai} glow={onAir} seated />
      <Furniture x={x + 0.3} y={y + 1.05} w={1.6} d={0.75} h={0.7} />
      <Monitor x={x + 0.7} y={y + 1.08} z={0.7} w={0.8} lit={onAir} />
    </g>
  );

  return (
    <g>
      {selected && <Selection x={x} y={y} w={B} d={B} />}
      {body}
      <OnAirSign at={[cx, y, 1.62]} lit={onAir} pulse={lights.busy} size={0.8} />
      {agent.tasks_30m > 0 && (
        <CountBadge at={[x + 0.25, y, 1.72]} count={agent.tasks_30m} color={c.ai}
          title={`${agent.tasks_30m} conversaciones en los últimos 30 min`} />
      )}
      <ClipLed at={[x + B - 0.25, y, 1.5]} lit={lights.clip} />
      <VuMeter at={[x + 0.2, y + B - 0.2, 0.06]} level={vuLevel(agent.turns_15m, lights.busy)} />
    </g>
  );
}

// ── Puesto de un asesor ──────────────────────────────────────────────────────
export const DESK_W = 2.4;
export const DESK_D = 2.3;
export const PRESENCE_WORD = { online: 'Disponible', paused: 'En pausa', offline: 'Desconectado' };

export function HumanDesk({ x, y, human, selected }) {
  const { P, s, c } = useIso();
  const present = human.presence !== 'offline';
  const shirt = c.human[human.presence] || c.human.offline;
  const cases = human.open_cases || 0;
  const [bx, by] = P(x + 1.75, y + 1.55, 1.25);
  const [px, py] = P(x + 1.05, y + 0.7, 2.0);
  return (
    <g opacity={present ? 1 : 0.55}>
      {selected && <Selection x={x + 0.15} y={y + 0.25} w={DESK_W - 0.3} d={DESK_D - 0.4} />}
      {/* Silla: vacía si está desconectado */}
      <Box x={x + 0.8} y={y + 0.45} w={0.5} d={0.5} h={0.4} top={c.furnitureSide} left={c.rack} right={c.rack} />
      <Box x={x + 0.8} y={y + 0.4} z={0.4} w={0.5} d={0.08} h={0.5} top={c.furnitureSide} left={c.rack} right={c.rack} />
      {present && <Person x={x + 1.05} y={y + 0.7} color={shirt} seated />}
      <Furniture x={x + 0.3} y={y + 1.05} w={1.6} d={0.75} h={0.72} />
      <Monitor x={x + 0.55} y={y + 1.08} z={0.72} w={0.75} lit={present} />
      {Array.from({ length: Math.min(cases, 5) }, (_, i) => (
        <Box key={i} x={x + 1.45} y={y + 1.4} z={0.72 + i * 0.05} w={0.34} d={0.26} h={0.04}
          top={c.case} left="#b45309" right="#92400e" stroke="none" />
      ))}
      {cases > 0 && (
        <g transform={`translate(${bx.toFixed(1)},${by.toFixed(1)})`}>
          <circle r={8} fill={c.case} stroke="rgba(0,0,0,0.4)" />
          <text y={3.5} fontSize={10} fontWeight={800} textAnchor="middle" fill="#1f1300">{cases}</text>
        </g>
      )}
      {human.presence === 'paused' && (
        <g transform={`translate(${px.toFixed(1)},${py.toFixed(1)})`}>
          <circle r={0.28 * s} fill="#1f2937" stroke={c.human.paused} strokeWidth={1.5} />
          <text y={0.1 * s} fontSize={0.28 * s} textAnchor="middle" fill={c.human.paused} fontWeight={800}>II</text>
        </g>
      )}
    </g>
  );
}

// ── Lobby: la cola ───────────────────────────────────────────────────────────
export const LOBBY_SEATS = 12;

export function Lobby({ room, queued }) {
  const { c, detail } = useIso();
  const { x, y, w } = room;
  const sofaW = (w - 1.4) / 2;
  const rows = [y + 0.45, y + 2.6];
  const seats = [];
  rows.forEach(ry => [x + 0.5, x + 0.9 + sofaW].forEach(sx => {
    for (let i = 0; i < 3; i++) seats.push([sx + sofaW * (i + 0.5) / 3, ry + 0.45]);
  }));
  const shown = Math.min(queued, LOBBY_SEATS);
  const overflow = queued - shown;
  return (
    <g>
      {rows.map((ry, r) => [x + 0.5, x + 0.9 + sofaW].map((sx, k) => (
        <g key={`${r}${k}`}>
          <Box x={sx} y={ry} w={sofaW} d={0.22} h={0.75} top={c.panel} left={c.panelAlt} right={c.panel} />
          <Box x={sx} y={ry + 0.22} w={sofaW} d={0.7} h={0.32} top={c.panel} left={c.panelAlt} right={c.rack} />
        </g>
      )))}
      {seats.slice(0, shown).map(([sx, sy], i) => <Person key={i} x={sx} y={sy} z={0.3} kind="lead" seated />)}
      {overflow > 0 && <Label at={[x + w / 2, y + 4.5, 0.2]} text={`+${overflow} más en cola`} size={11} color={c.case} />}
      {queued === 0 && <Label at={[x + w / 2, y + 4.5, 0.2]} text="Nadie esperando" size={detail ? 11 : 9} color={c.muted} weight={500} />}
    </g>
  );
}
